export const REPORT_COLORS = {
  ink: "#0b1f3a",
  inkSoft: "#5b6f86",
  line: "#e3e9f1",
  bg: "#f6f8fb",
  card: "#ffffff",
  green: "#10a371",
  greenSoft: "#dff7ec",
  amber: "#c98a14",
  amberSoft: "#fdecc8",
  red: "#c2384a",
  redSoft: "#fde0e4",
  blue: "#1f5fd0",
  blueSoft: "#e3edff",
  slate: "#9ba8be",
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const normalizeSavedScenario = (scenario) =>
  scenario && typeof scenario === "object" ? scenario : {};

const manualCommitmentFromTransaction = (transaction) => ({
  id: `manual-dsr-${transaction.id}`,
  manual: true,
  transaction_id: transaction.id,
  active: true,
  keep: true,
  counterparty: transaction.counterparty || transaction.description || "Manual debit",
  cadence: "Manual",
  monthly_amount: Math.abs(toNumber(transaction.amount)),
  months_seen: 1,
  confidence: 1,
  confidence_label: "Manual",
  tag: "Manual DSR",
  likely_lender: true,
  notes: [
    transaction.date,
    transaction.bank,
    transaction.account_number,
    transaction.description,
  ]
    .filter(Boolean)
    .join(" · "),
  source_transaction: transaction,
});

const manualLumpSumFromTransaction = (transaction) => ({
  id: `manual-revenue-${transaction.id}`,
  manual: true,
  transaction_id: transaction.id,
  included: true,
  include_in_revenue: true,
  date: transaction.date,
  amount: Math.abs(toNumber(transaction.amount)),
  currency: transaction.currency,
  description: transaction.description,
  classification: "Manual revenue",
  reason: [
    transaction.bank,
    transaction.account_number,
    transaction.month_label,
  ]
    .filter(Boolean)
    .join(" · "),
  source_transaction: transaction,
});

export const formatMoney = (amount, currency = "SGD", digits = 2) => {
  if (amount === null || amount === undefined || amount === "") {
    return "-";
  }

  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) {
    return String(amount);
  }

  try {
    return numeric.toLocaleString("en-US", {
      style: "currency",
      currency: currency || "SGD",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  } catch (error) {
    return `${currency || ""} ${numeric.toLocaleString("en-US", {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    })}`.trim();
  }
};

export const formatPercent = (value, digits = 1) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "-";
  }

  return `${numeric.toFixed(digits)}%`;
};

export const formatMultiple = (value, digits = 2) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "-";
  }

  return `${numeric.toFixed(digits)}x`;
};

export const formatShortMonth = (value) => {
  if (!value) {
    return "-";
  }

  return String(value).split(" ")[0];
};

export const buildInitialReportScenario = (report, savedScenario = null) => {
  const dashboard = report?.dashboard || {};
  const coverage = dashboard.coverage || {};
  const defaults = coverage.stress_defaults || {};
  const saved = normalizeSavedScenario(savedScenario);

  return {
    commitments: Object.fromEntries(
      (dashboard.commitments || []).map((item) => [
        item.id,
        saved.commitments?.[item.id] !== undefined
          ? Boolean(saved.commitments[item.id])
          : item.keep !== false,
      ]),
    ),
    lumpSums: Object.fromEntries(
      (dashboard.lump_sum_deposits?.items || []).map((item) => [
        item.id,
        saved.lumpSums?.[item.id] !== undefined
          ? Boolean(saved.lumpSums[item.id])
          : item.include_in_revenue !== false,
      ]),
    ),
    stress: {
      loanAmount:
        saved.stress?.loanAmount !== undefined
          ? toNumber(saved.stress.loanAmount)
          : toNumber(defaults.loan_amount),
      loanTenorMonths: Math.max(
        1,
        saved.stress?.loanTenorMonths !== undefined
          ? toNumber(saved.stress.loanTenorMonths)
          : toNumber(defaults.loan_tenor_months) || 24,
      ),
      monthlyRatePercent: Math.max(
        0,
        saved.stress?.monthlyRatePercent !== undefined
          ? toNumber(saved.stress.monthlyRatePercent)
          : toNumber(defaults.monthly_rate_percent) || 2,
      ),
    },
    manualCommitments: Array.isArray(saved.manualCommitments)
      ? saved.manualCommitments
      : [],
    manualLumpSums: Array.isArray(saved.manualLumpSums)
      ? saved.manualLumpSums
      : [],
  };
};

export const getDsrBand = (value) => {
  if (value <= 40) return "green";
  if (value <= 60) return "amber";
  return "red";
};

export const getFccBand = (value) => {
  if (value >= 2) return "green";
  if (value >= 1) return "amber";
  return "red";
};

export const bandColors = (band) => {
  if (band === "green") {
    return { text: REPORT_COLORS.green, background: REPORT_COLORS.greenSoft };
  }

  if (band === "amber") {
    return { text: REPORT_COLORS.amber, background: REPORT_COLORS.amberSoft };
  }

  return { text: REPORT_COLORS.red, background: REPORT_COLORS.redSoft };
};

export const getDsrLabel = (value) => {
  if (value <= 40) return "Strong";
  if (value <= 60) return "Watch";
  return "Stretched";
};

export const getFccLabel = (value) => {
  if (value >= 2) return "Strong";
  if (value >= 1) return "Tight";
  return "Fail";
};

export const getHeatmapColor = (value, min, max) => {
  if (!Number.isFinite(value)) {
    return "#eef2f7";
  }

  const lower = Number.isFinite(min) ? min : value;
  const upper = Number.isFinite(max) && max !== min ? max : value + 1;
  const t = Math.max(0, Math.min(1, (value - lower) / (upper - lower)));
  const stops = [
    [194, 56, 74],
    [232, 138, 85],
    [245, 196, 65],
    [164, 217, 124],
    [16, 163, 113],
  ];
  const segment = t * (stops.length - 1);
  const index = Math.floor(segment);
  const fraction = segment - index;
  const start = stops[index];
  const end = stops[Math.min(index + 1, stops.length - 1)];
  const mixed = start.map((channel, channelIndex) =>
    Math.round(channel + (end[channelIndex] - channel) * fraction),
  );

  return `rgb(${mixed.join(",")})`;
};

export const deriveReportScenario = (report, scenario) => {
  const dashboard = report?.dashboard || {};
  const coverage = dashboard.coverage || {};
  const commitments = (dashboard.commitments || []).map((item) => ({
    ...item,
    active:
      scenario?.commitments?.[item.id] !== undefined
        ? Boolean(scenario.commitments[item.id])
        : item.keep !== false,
  }));
  const manualCommitments = (scenario?.manualCommitments || [])
    .filter(Boolean)
    .map((item) => ({
      ...item,
      active: item.active !== false,
      keep: item.keep !== false,
    }));
  const lumpSums = (dashboard.lump_sum_deposits?.items || []).map((item) => ({
    ...item,
    included:
      scenario?.lumpSums?.[item.id] !== undefined
        ? Boolean(scenario.lumpSums[item.id])
        : item.include_in_revenue !== false,
  }));
  const manualLumpSums = (scenario?.manualLumpSums || [])
    .filter(Boolean)
    .map((item) => ({
      ...item,
      included: item.included !== false,
      include_in_revenue: item.include_in_revenue !== false,
    }));

  const monthsCoveredCount = Math.max(report?.overview?.months_covered_count || 0, 1);
  const allCommitments = [...commitments, ...manualCommitments];
  const allLumpSums = [...lumpSums, ...manualLumpSums];
  const activeCommitments = allCommitments.filter((item) => item.active);
  const excludedLumpSums = allLumpSums.filter(
    (item) => !item.manual && !item.included,
  );
  const includedManualRevenue = manualLumpSums.filter((item) => item.included);
  const monthlyCommitment = activeCommitments.reduce(
    (sum, item) => sum + toNumber(item.monthly_amount),
    0,
  );
  const excludedRevenueTotal = excludedLumpSums.reduce(
    (sum, item) => sum + toNumber(item.amount),
    0,
  );
  const manualRevenueTotal = includedManualRevenue.reduce(
    (sum, item) => sum + toNumber(item.amount),
    0,
  );
  const rawRevenueTotal = toNumber(coverage.raw_revenue_total);
  const adjustedRevenueTotal = Math.max(
    0,
    rawRevenueTotal - excludedRevenueTotal + manualRevenueTotal,
  );
  const adjustedMonthlyRevenue = adjustedRevenueTotal / monthsCoveredCount;
  const averageEndMonthBalance = toNumber(coverage.average_end_month_balance);
  const baseDsrPercent =
    adjustedMonthlyRevenue > 0 ? (monthlyCommitment / adjustedMonthlyRevenue) * 100 : 0;
  const baseFccRatio =
    monthlyCommitment > 0 ? averageEndMonthBalance / monthlyCommitment : 0;

  const stress = {
    loanAmount: toNumber(scenario?.stress?.loanAmount),
    loanTenorMonths: Math.max(1, toNumber(scenario?.stress?.loanTenorMonths) || 24),
    monthlyRatePercent: Math.max(0, toNumber(scenario?.stress?.monthlyRatePercent) || 2),
  };
  const principalPayment =
    stress.loanTenorMonths > 0 ? stress.loanAmount / stress.loanTenorMonths : 0;
  const interestPayment = stress.loanAmount * (stress.monthlyRatePercent / 100);
  const monthlyPayment = principalPayment + interestPayment;
  const stressDsrPercent =
    adjustedMonthlyRevenue > 0
      ? ((monthlyCommitment + monthlyPayment) / adjustedMonthlyRevenue) * 100
      : 0;
  const stressFccRatio =
    monthlyCommitment + monthlyPayment > 0
      ? averageEndMonthBalance / (monthlyCommitment + monthlyPayment)
      : 0;

  const concentration = dashboard.drilldowns?.inflow_concentration || [];
  const totalConcentrationAmount = concentration.reduce(
    (sum, item) => sum + toNumber(item.amount),
    0,
  );
  const concentrationSegments = concentration.map((item, index) => ({
    ...item,
    sharePercent:
      item.share_percent !== undefined
        ? toNumber(item.share_percent)
        : totalConcentrationAmount > 0
          ? (toNumber(item.amount) / totalConcentrationAmount) * 100
          : 0,
    color: [
      REPORT_COLORS.blue,
      REPORT_COLORS.green,
      REPORT_COLORS.amber,
      "#7a5cf0",
      REPORT_COLORS.slate,
    ][index % 5],
  }));

  return {
    commitments: allCommitments,
    lumpSums: allLumpSums,
    activeCommitments,
    excludedLumpSums,
    adjustedRevenueTotal,
    adjustedMonthlyRevenue,
    excludedRevenueTotal,
    manualRevenueTotal,
    monthlyCommitment,
    averageEndMonthBalance,
    baseDsrPercent,
    baseFccRatio,
    headroomToSixtyDsr: Math.max(0, adjustedMonthlyRevenue * 0.6 - monthlyCommitment),
    stress,
    stressBreakdown: {
      principalPayment,
      interestPayment,
      monthlyPayment,
      dsrPercent: stressDsrPercent,
      fccRatio: stressFccRatio,
    },
    dsrBand: getDsrBand(baseDsrPercent),
    fccBand: getFccBand(baseFccRatio),
    stressDsrBand: getDsrBand(stressDsrPercent),
    stressFccBand: getFccBand(stressFccRatio),
    concentrationSegments,
  };
};

export const buildManualCommitmentFromTransaction = manualCommitmentFromTransaction;

export const buildManualLumpSumFromTransaction = manualLumpSumFromTransaction;
